/**
 * Mobile UX Utilities
 * Helpers for mobile-first, touch-friendly user experiences
 * Following WCAG 2.1 AA standards and iOS/Android guidelines
 * 
 * Standards:
 * - Touch targets: 48x48px minimum (iOS: 44px, Android: 48px)
 * - Spacing: 8px minimum between touch targets
 * - Text: 16px minimum to prevent iOS zoom
 * - Safe areas: iOS notch/home indicator support
 */

/**
 * Touch Target Sizes (following docs/08-03-ux-standards.md)
 */
export const TOUCH_TARGETS = {
  // Minimum touch target size (48x48px for Android, 44x44px for iOS)
  // We use 48px as the standard to satisfy both
  MIN_SIZE: 48,
  
  // Recommended sizes for different button types
  ICON_BUTTON: 48,
  TEXT_BUTTON: { minHeight: 48, minWidth: 88 },
  FAB: 56,
  FAB_EXTENDED: { minHeight: 48, minWidth: 120 },
  
  // Minimum spacing between touch targets
  MIN_SPACING: 8,
};

/**
 * Typography sizes to prevent iOS auto-zoom
 * iOS zooms in when input font-size < 16px
 */
export const MOBILE_TYPOGRAPHY = {
  INPUT_MIN_SIZE: '16px',
  BODY_MIN_SIZE: '14px',
  CAPTION_MIN_SIZE: '12px',
};

/**
 * Safe area insets for iOS notch and home indicator
 */
export const SAFE_AREA = {
  top: 'env(safe-area-inset-top, 0px)',
  right: 'env(safe-area-inset-right, 0px)',
  bottom: 'env(safe-area-inset-bottom, 0px)',
  left: 'env(safe-area-inset-left, 0px)',
};

/**
 * Get touch-friendly button styles
 * Ensures minimum 48x48px touch target
 * 
 * @param {Object} options - Configuration options
 * @param {number} options.size - Custom size (defaults to 48px)
 * @param {boolean} options.fullWidth - Make button full width
 * @returns {Object} MUI sx prop object
 */
export const getTouchButtonStyles = ({ size = TOUCH_TARGETS.MIN_SIZE, fullWidth = false } = {}) => ({
  minWidth: fullWidth ? '100%' : size,
  minHeight: size,
  padding: size === TOUCH_TARGETS.MIN_SIZE ? '12px 16px' : undefined,
  
  // Ensure touch target even if content is smaller
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    minWidth: size,
    minHeight: size,
    pointerEvents: 'none',
  },
});

/**
 * Get touch-friendly IconButton styles
 * Ensures minimum 48x48px touch target with proper padding
 * 
 * @param {number} iconSize - Size of the icon (default: 24px)
 * @returns {Object} MUI sx prop object
 */
export const getTouchIconButtonStyles = (iconSize = 24) => {
  const padding = (TOUCH_TARGETS.ICON_BUTTON - iconSize) / 2;
  
  return {
    width: TOUCH_TARGETS.ICON_BUTTON,
    height: TOUCH_TARGETS.ICON_BUTTON,
    padding: `${padding}px`,
    
    '& svg': {
      fontSize: iconSize,
    },
  };
};

/**
 * Get mobile-optimized input styles
 * Prevents iOS auto-zoom and provides comfortable touch targets
 * 
 * @param {Object} options - Configuration options
 * @returns {Object} MUI sx prop object
 */
export const getMobileInputStyles = (options = {}) => ({
  '& .MuiInputBase-root': {
    minHeight: TOUCH_TARGETS.MIN_SIZE,
    fontSize: MOBILE_TYPOGRAPHY.INPUT_MIN_SIZE,
  },
  
  '& .MuiInputBase-input': {
    fontSize: MOBILE_TYPOGRAPHY.INPUT_MIN_SIZE,
    padding: '12px 14px',
  },
  
  '& .MuiInputLabel-root': {
    fontSize: MOBILE_TYPOGRAPHY.INPUT_MIN_SIZE,
  },
  
  // Prevent iOS zoom on focus
  '@supports (-webkit-touch-callout: none)': {
    '& .MuiInputBase-input': {
      fontSize: '16px !important',
    },
  },
});

/**
 * Get safe area padding for iOS devices
 * Adds padding to avoid notch and home indicator
 * 
 * @param {Object} options - Which sides to apply safe area
 * @param {boolean} options.top - Apply to top
 * @param {boolean} options.right - Apply to right
 * @param {boolean} options.bottom - Apply to bottom
 * @param {boolean} options.left - Apply to left
 * @returns {Object} MUI sx prop object
 */
export const getSafeAreaPadding = ({ top = false, right = false, bottom = false, left = false } = {}) => ({
  ...(top && { paddingTop: `calc(16px + ${SAFE_AREA.top})` }),
  ...(right && { paddingRight: `calc(16px + ${SAFE_AREA.right})` }),
  ...(bottom && { paddingBottom: `calc(16px + ${SAFE_AREA.bottom})` }),
  ...(left && { paddingLeft: `calc(16px + ${SAFE_AREA.left})` }),
});

/**
 * Get mobile-optimized list item styles
 * Ensures comfortable touch targets for list items
 * 
 * @returns {Object} MUI sx prop object
 */
export const getMobileListItemStyles = () => ({
  minHeight: TOUCH_TARGETS.MIN_SIZE,
  padding: '12px 16px',
  
  '& .MuiListItemIcon-root': {
    minWidth: 40,
  },
  
  '& .MuiListItemText-primary': {
    fontSize: MOBILE_TYPOGRAPHY.INPUT_MIN_SIZE,
  },
  
  '& .MuiListItemText-secondary': {
    fontSize: MOBILE_TYPOGRAPHY.BODY_MIN_SIZE,
  },
});

/**
 * Check if device is iOS
 * Useful for applying iOS-specific fixes
 * 
 * @returns {boolean} True if iOS device
 */
export const isIOS = () => {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
};

/**
 * Check if device is Android
 * 
 * @returns {boolean} True if Android device
 */
export const isAndroid = () => {
  return /Android/.test(navigator.userAgent);
};

/**
 * Check if device is mobile (iOS or Android)
 * 
 * @returns {boolean} True if mobile device
 */
export const isMobileDevice = () => {
  return isIOS() || isAndroid();
};

/**
 * Get viewport height that accounts for mobile browser chrome
 * Mobile browsers have dynamic viewport height due to address bar
 * 
 * @returns {string} CSS value for viewport height
 */
export const getMobileViewportHeight = () => {
  // Use dvh (dynamic viewport height) if supported, fallback to vh
  if (CSS.supports('height', '100dvh')) {
    return '100dvh';
  }
  return '100vh';
};

/**
 * Prevent body scroll (useful for modals on mobile)
 * Handles iOS body scroll lock properly
 */
export const preventBodyScroll = () => {
  const scrollY = window.scrollY;
  document.body.style.position = 'fixed';
  document.body.style.top = `-${scrollY}px`;
  document.body.style.width = '100%';
};

/**
 * Restore body scroll
 */
export const restoreBodyScroll = () => {
  const scrollY = document.body.style.top;
  document.body.style.position = '';
  document.body.style.top = '';
  document.body.style.width = '';
  window.scrollTo(0, parseInt(scrollY || '0') * -1);
};

/**
 * Get reduced motion preference
 * Respects user's accessibility settings
 * 
 * @returns {boolean} True if user prefers reduced motion
 */
export const prefersReducedMotion = () => {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
};

/**
 * Get animation duration based on reduced motion preference
 * 
 * @param {number} normalDuration - Normal animation duration in ms
 * @returns {number} Duration in ms (0 if reduced motion preferred)
 */
export const getAnimationDuration = (normalDuration = 300) => {
  return prefersReducedMotion() ? 0 : normalDuration;
};

/**
 * Get mobile-optimized modal styles
 * Full screen on mobile, centered on desktop
 * 
 * @returns {Object} MUI sx prop object
 */
export const getMobileModalStyles = () => ({
  position: 'absolute',
  top: { xs: 0, sm: '50%' },
  left: { xs: 0, sm: '50%' },
  transform: { xs: 'none', sm: 'translate(-50%, -50%)' },
  width: { xs: '100%', sm: 'auto' },
  height: { xs: '100%', sm: 'auto' },
  maxWidth: { sm: 600 },
  maxHeight: { sm: '90vh' },
  bgcolor: 'background.paper',
  borderRadius: { xs: 0, sm: 2 },
  boxShadow: 24,
  overflow: 'auto',
  
  // Safe area for iOS
  ...getSafeAreaPadding({ top: true, bottom: true }),
});

/**
 * Get mobile-optimized bottom sheet styles
 * Slides up from bottom on mobile
 * 
 * @returns {Object} MUI sx prop object
 */
export const getBottomSheetStyles = () => ({
  position: 'fixed',
  bottom: 0,
  left: 0,
  right: 0,
  bgcolor: 'background.paper',
  borderTopLeftRadius: 16,
  borderTopRightRadius: 16,
  boxShadow: '0 -4px 20px rgba(0,0,0,0.1)',
  maxHeight: '90vh',
  overflow: 'auto',
  
  // Safe area for iOS home indicator
  paddingBottom: SAFE_AREA.bottom,
});

/**
 * Haptic feedback (iOS only)
 * Provides tactile feedback for touch interactions
 * 
 * @param {string} type - Type of feedback ('light', 'medium', 'heavy', 'selection', 'success', 'warning', 'error')
 */
export const triggerHaptic = (type = 'light') => {
  // Check if vibration API is available
  if (typeof window === 'undefined' || !window.navigator || !window.navigator.vibrate) {
    return; // Gracefully fail if API not available
  }
  
  const patterns = {
    light: [10],
    medium: [20],
    heavy: [30],
    selection: [10],
    success: [10, 50, 10],
    warning: [20, 50, 20],
    error: [30, 50, 30],
  };
  
  const pattern = patterns[type] || patterns.light;
  
  try {
    window.navigator.vibrate(pattern);
  } catch (error) {
    // Silently fail if vibration fails
    console.debug('Haptic feedback not supported:', error);
  }
};
